// Phase HR-003 Wave 6 — Training Effectiveness (doc Study #8).
//
// Two tabs: Programs (re-usable masters) + Sessions (scheduled deliveries
// with attendance capture). Pre/post test scores + supervisor validation
// + baseline/30/60/90-day KPI snapshots = effectiveness over time.

import { useMemo, useState } from 'react'
import {
  Alert, App, Button, DatePicker, Drawer, Empty, Form, Input, InputNumber,
  Modal, Select, Space, Switch, Table, Tabs, Tag, Typography,
} from 'antd'
import { PlusOutlined, SaveOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import {
  trainingApi, type AttendanceRow, type TrainingProgram,
  type TrainingSession, type TrainingSessionStatus,
} from '@/features/hr/trainingFloorApi'
import { departmentsApi } from '@/features/masters/departments/api'
import { employeesApi } from '@/features/hr/api'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'
import { useHrMobilePolish } from '@/shared/styles/hrMobilePolish'

const { Text } = Typography

export default function TrainingPage() {
  useHrMobilePolish()
  const [tab, setTab] = useState<'programs' | 'sessions'>('sessions')
  return (
    <div className="hr-mobile-root">
      <PageHeader
        title="Training Effectiveness"
        subtitle="Doc §8 — Programs + Sessions + pre/post test + KPI delta over 30/60/90 days."
      />
      <Tabs activeKey={tab} onChange={(k) => setTab(k as typeof tab)}
        items={[
          { key: 'sessions', label: 'Sessions',   children: <SessionsTab /> },
          { key: 'programs', label: 'Programs',   children: <ProgramsTab /> },
        ]} />
    </div>
  )
}

// ─── Programs tab ───────────────────────────────────────────────────

function ProgramsTab() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const canCreate = usePermission('employee.create')
  const [modal, setModal] = useState(false)
  const [form] = Form.useForm()

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['training-programs'],
    queryFn: () => trainingApi.listPrograms(true),
  })

  const createMut = useMutation({
    mutationFn: trainingApi.createProgram,
    onSuccess: () => {
      message.success('Program created.')
      qc.invalidateQueries({ queryKey: ['training-programs'] })
      setModal(false); form.resetFields()
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Failed'),
  })

  return (
    <>
      {canCreate && (
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModal(true)}
          style={{ marginBottom: 12 }}>
          New program
        </Button>
      )}
      <Table<TrainingProgram>
        size="small" rowKey="id" loading={isLoading} dataSource={rows}
        pagination={{ pageSize: 20 }}
        columns={[
          { title: 'Code', dataIndex: 'program_code', width: 120 },
          { title: 'Name', dataIndex: 'program_name' },
          { title: 'Category', dataIndex: 'category', width: 120 },
          { title: 'Target KPI', dataIndex: 'target_kpi_code', width: 160,
            render: (v: string | null) => v ? <Tag>{v}</Tag> : '—' },
          { title: 'Direction', dataIndex: 'expected_kpi_direction', width: 140 },
          { title: 'Duration', dataIndex: 'duration_minutes', width: 90,
            render: (v: number | null) => v ? `${v} min` : '—' },
        ]}
      />
      <Modal open={modal} onCancel={() => setModal(false)}
        title="New training program"
        onOk={() => form.validateFields().then(createMut.mutate)}
        confirmLoading={createMut.isPending}
        okText="Create" width={560}>
        <Form form={form} layout="vertical">
          <Form.Item label="Program code" name="program_code"
            rules={[{ required: true }]}>
            <Input placeholder="e.g. PIECING-QA-01" />
          </Form.Item>
          <Form.Item label="Program name" name="program_name"
            rules={[{ required: true }]}>
            <Input placeholder="Piecing quality discipline" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Space style={{ width: '100%' }}>
            <Form.Item label="Category" name="category" style={{ flex: 1 }}>
              <Select allowClear placeholder="Category"
                options={[
                  { value: 'QUALITY',     label: 'Quality' },
                  { value: 'SAFETY',      label: 'Safety' },
                  { value: 'PRODUCTIVITY', label: 'Productivity' },
                  { value: 'INDUCTION',   label: 'Induction' },
                  { value: 'MAINTENANCE', label: 'Maintenance' },
                  { value: 'OTHER',       label: 'Other' },
                ]} />
            </Form.Item>
            <Form.Item label="Duration (min)" name="duration_minutes">
              <InputNumber min={5} max={600} />
            </Form.Item>
          </Space>
          <Space>
            <Form.Item label="Target KPI code" name="target_kpi_code">
              <Input placeholder="e.g. operator_error_rate" />
            </Form.Item>
            <Form.Item label="Direction" name="expected_kpi_direction">
              <Select allowClear style={{ width: 180 }}
                options={[
                  { value: 'HIGHER_IS_BETTER', label: 'Higher is better' },
                  { value: 'LOWER_IS_BETTER',  label: 'Lower is better' },
                ]} />
            </Form.Item>
          </Space>
          <Form.Item label="Materials URL" name="materials_url">
            <Input placeholder="Link to slides / SOP / video" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

// ─── Sessions tab ───────────────────────────────────────────────────

function SessionsTab() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const canCreate = usePermission('employee.create')
  const [modal, setModal] = useState(false)
  const [statusFilter, setStatusFilter] = useState<TrainingSessionStatus | 'ALL'>('ALL')
  const [openSession, setOpenSession] = useState<TrainingSession | null>(null)
  const [form] = Form.useForm()

  const { data: programs = [] } = useQuery({
    queryKey: ['training-programs'],
    queryFn: () => trainingApi.listPrograms(true),
  })
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['training-sessions', plantId, statusFilter],
    queryFn: () => trainingApi.listSessions({
      plant_id: plantId,
      status: statusFilter === 'ALL' ? undefined : statusFilter,
      limit: 200,
    }),
    enabled: !!plantId,
  })

  const createMut = useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationFn: (values: any) => {
      if (!plantId) throw new Error('Plant missing')
      return trainingApi.createSession({
        program_id: values.program_id,
        plant_id: plantId,
        scheduled_date: (values.scheduled_date as Dayjs).format('YYYY-MM-DD'),
        batch_code: values.batch_code || null,
        trainer_name_external: values.trainer_name_external || null,
        location: values.location || null,
      })
    },
    onSuccess: () => {
      message.success('Session created.')
      qc.invalidateQueries({ queryKey: ['training-sessions'] })
      setModal(false); form.resetFields()
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Failed'),
  })

  return (
    <>
      <Space wrap style={{ marginBottom: 12 }}>
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModal(true)}>
            New session
          </Button>
        )}
        <div className="hr-filter-chips">
          {(['ALL', 'PLANNED', 'DELIVERED', 'CANCELLED', 'RESCHEDULED'] as const).map((s) => (
            <div key={s} role="button"
              className={`hr-filter-chip${statusFilter === s ? ' hr-filter-chip--active' : ''}`}
              onClick={() => setStatusFilter(s)}>
              {s.charAt(0) + s.slice(1).toLowerCase()}
            </div>
          ))}
        </div>
      </Space>
      <Table<TrainingSession>
        size="small" rowKey="id" loading={isLoading} dataSource={rows}
        pagination={{ pageSize: 20 }}
        onRow={(r) => ({ onClick: () => setOpenSession(r), style: { cursor: 'pointer' } })}
        columns={[
          { title: 'No', dataIndex: 'session_no', width: 130, fixed: 'left' },
          { title: 'Date', dataIndex: 'scheduled_date', width: 110,
            render: (v: string) => dayjs(v).format('DD MMM') },
          { title: 'Program', dataIndex: 'program_name' },
          { title: 'Batch', dataIndex: 'batch_code', width: 120 },
          { title: 'Status', dataIndex: 'status', width: 110,
            render: (v: TrainingSessionStatus) => <Tag color={
              v === 'DELIVERED' ? 'green' : v === 'PLANNED' ? 'blue'
              : v === 'CANCELLED' ? 'red' : 'gold'}>{v}</Tag> },
          { title: 'Attended', width: 110,
            render: (_, r) => `${r.attended_count}/${r.attendee_count}` },
          { title: 'Avg pre/post', width: 140,
            render: (_, r) => {
              if (!r.avg_pre_score && !r.avg_post_score) return '—'
              return `${r.avg_pre_score ?? '—'} → ${r.avg_post_score ?? '—'}`
            } },
        ]}
        scroll={{ x: 900 }}
      />

      <Modal open={modal} onCancel={() => setModal(false)}
        title="New training session" width={520}
        onOk={() => form.validateFields().then(createMut.mutate)}
        confirmLoading={createMut.isPending}>
        <Form form={form} layout="vertical" initialValues={{ scheduled_date: dayjs() }}>
          <Form.Item label="Program" name="program_id" rules={[{ required: true }]}>
            <Select placeholder="Pick a program"
              options={programs.map((p) => ({ value: p.id, label: p.program_name }))} />
          </Form.Item>
          <Space>
            <Form.Item label="Scheduled date" name="scheduled_date" rules={[{ required: true }]}>
              <DatePicker style={{ width: 160 }} />
            </Form.Item>
            <Form.Item label="Batch code" name="batch_code">
              <Input placeholder="e.g. RF-SHIFT-B-2026-05" />
            </Form.Item>
          </Space>
          <Form.Item label="Trainer name (external)" name="trainer_name_external">
            <Input placeholder="Or paste a trainer's email" />
          </Form.Item>
          <Form.Item label="Location" name="location">
            <Input placeholder="Training room / floor area" />
          </Form.Item>
        </Form>
      </Modal>

      {openSession && (
        <AttendanceDrawer session={openSession}
          onClose={() => setOpenSession(null)} />
      )}
    </>
  )
}

// ─── Attendance drawer ─────────────────────────────────────────────

function AttendanceDrawer({ session, onClose }: {
  session: TrainingSession; onClose: () => void
}) {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const [drafts, setDrafts] = useState<Record<string, AttendanceRow>>({})

  const { data: deptsData } = useQuery({
    queryKey: ['train-depts', plantId],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 500, plant_id: plantId }),
    enabled: !!plantId,
  })
  const { data: empsData } = useQuery({
    queryKey: ['train-emps', plantId],
    queryFn: () => employeesApi.list({
      plant_id: plantId, is_active: true, page: 1, size: 500,
    }),
    enabled: !!plantId,
  })

  const { data: existing = [] } = useQuery({
    queryKey: ['training-attendance', session.id],
    queryFn: () => trainingApi.getAttendance(session.id),
  })

  // Seed drafts from existing rows
  useMemo(() => {
    const next: Record<string, AttendanceRow> = {}
    for (const r of existing) next[r.employee_id] = { ...r }
    setDrafts(next)
  }, [existing])

  const addRows = (employeeIds: string[]) => {
    setDrafts((prev) => {
      const next = { ...prev }
      for (const id of employeeIds) {
        if (!next[id]) next[id] = { employee_id: id, attended: true }
      }
      return next
    })
  }

  const patch = (id: string, p: Partial<AttendanceRow>) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...p } }))
  }

  const saveMut = useMutation({
    mutationFn: () => trainingApi.upsertAttendance(session.id, Object.values(drafts)),
    onSuccess: () => {
      message.success('Attendance saved.')
      qc.invalidateQueries({ queryKey: ['training-attendance', session.id] })
      qc.invalidateQueries({ queryKey: ['training-sessions'] })
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Save failed'),
  })

  const draftList = Object.values(drafts)
  const empById = new Map((empsData?.items ?? []).map((e) => [e.id, e]))

  return (
    <Drawer
      open onClose={onClose} placement="right"
      width={Math.min(720, window.innerWidth - 24)}
      title={<Space>
        <Text strong>{session.session_no}</Text>
        <Tag>{session.program_name}</Tag>
        <Tag>{dayjs(session.scheduled_date).format('DD MMM')}</Tag>
      </Space>}
      extra={
        <Space>
          <Button type="primary" icon={<SaveOutlined />}
            loading={saveMut.isPending}
            disabled={draftList.length === 0}
            onClick={() => saveMut.mutate()}>
            Save {draftList.length}
          </Button>
        </Space>
      }>
      <Space style={{ marginBottom: 12 }}>
        <Select mode="multiple" allowClear placeholder="Add employees"
          style={{ minWidth: 300 }}
          showSearch
          filterOption={(input, opt) =>
            (opt?.label as string)?.toLowerCase().includes(input.toLowerCase())}
          options={(empsData?.items ?? []).map((e) => ({
            value: e.id,
            label: `${e.emp_name} (${e.emp_code})`,
          }))}
          onChange={(ids: string[]) => addRows(ids)} />
      </Space>

      {draftList.length === 0 ? (
        <Empty description="No attendees yet — pick from dropdown above." />
      ) : (
        <Table<AttendanceRow>
          size="small" rowKey="employee_id" dataSource={draftList}
          pagination={false}
          columns={[
            { title: 'Worker', width: 200, render: (_, r) => {
              const e = empById.get(r.employee_id)
              return e ? `${e.emp_name} (${e.emp_code})` : r.employee_id.slice(0, 8)
            } },
            { title: 'Attended', width: 90, render: (_, r) => (
              <Switch size="small" checked={r.attended}
                onChange={(v) => patch(r.employee_id, { attended: v })} />
            ) },
            { title: 'Pre-score', width: 110, render: (_, r) => (
              <InputNumber size="small" value={r.pre_test_score ?? null}
                onChange={(v) => patch(r.employee_id, { pre_test_score: v })}
                min={0} max={100} controls={false} />
            ) },
            { title: 'Post-score', width: 110, render: (_, r) => (
              <InputNumber size="small" value={r.post_test_score ?? null}
                onChange={(v) => patch(r.employee_id, { post_test_score: v })}
                min={0} max={100} controls={false} />
            ) },
            { title: 'Pass %', width: 90, render: (_, r) => (
              <InputNumber size="small" value={r.pass_threshold_pct ?? null}
                onChange={(v) => patch(r.employee_id, { pass_threshold_pct: v })}
                min={0} max={100} controls={false} />
            ) },
            { title: 'Notes', render: (_, r) => (
              <Input size="small" value={r.supervisor_validation_notes ?? ''}
                onChange={(e) => patch(r.employee_id,
                  { supervisor_validation_notes: e.target.value })}
                placeholder="Supervisor note" />
            ) },
          ]}
        />
      )}
      <Alert style={{ marginTop: 12 }} type="info" showIcon
        message="Baseline KPI / 30 / 60 / 90-day re-measure are captured later from a separate review screen."
      />
    </Drawer>
  )
}
